import { useContext } from 'react'
import { isActiveRequest, type MealPlanRequest } from '@/types/mealPlan'
import { MealPlanRequestsContext } from './MealPlanRequestsContext'

export interface UseGetMealPlanRequestsResult {
  // Raw streamed requests, newest first (currently at most one — see the slice).
  requests: MealPlanRequest[]
  // The most recent request, whatever its status — watch its status to react to a
  // processing → completed/failed transition, then join to the result by planId.
  latestRequest: MealPlanRequest | null
  // The newest request still in flight (pending or processing), or null.
  activeRequest: MealPlanRequest | null
  // The one-plan-at-a-time guard primitive: true while a request is in flight.
  hasActiveRequest: boolean
  loading: boolean
  error: string | null
}

export function useGetMealPlanRequests(): UseGetMealPlanRequestsResult {
  const context = useContext(MealPlanRequestsContext)

  if (!context) {
    throw new Error('useGetMealPlanRequests must be used within MealPlanRequestsProvider')
  }

  const { mealPlanRequests, loading, error } = context.state
  const latestRequest = mealPlanRequests[0] ?? null
  const activeRequest = mealPlanRequests.find(isActiveRequest) ?? null

  return {
    requests: mealPlanRequests,
    latestRequest,
    activeRequest,
    hasActiveRequest: activeRequest !== null,
    loading,
    error
  }
}
